import { useState, useMemo } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import CircularProgress from '@zenbusiness/zui/CircularProgress';
import ErrorState from '@zenbusiness/zui/ErrorState';
import PageContainer from '@zenbusiness/zui/PageContainer';
import PageHeader from '@zenbusiness/zui/PageHeader';
import { useTheme } from '@zenbusiness/zui/styles';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import { useOrderHistory } from '#client/hooks/useOrderHistory';
import isWebView from '#client/utils/isWebView';

import OrderTemplate from './components/OrderTemplate';
import { StyledCard } from './OrderHistory.styles';
import { getUniqueOrders, getUniqueYears, groupPlanWithTasksByYear } from './utils';

export default function OrderHistory() {
  const [selectedYear, setSelectedYear] = useState(null);
  const {
    getOrderTasks, getPaymentHistory, loading, error
  } = useOrderHistory();
  const { breakpoints } = useTheme();
  const md = useMediaQuery(breakpoints.up('md'));
  const navigate = useNavigate();
  const { businessEntityUuid } = useParams();

  const trackDownloadReceipts = useButtonPressedCallback({
    elementName: 'download_receipts_button',
    page: 'order_history'
  });

  // Compute expensive values with useMemo - must be called before early returns
  const orders = useMemo(() => getUniqueOrders(getOrderTasks || []), [getOrderTasks]);
  const years = useMemo(() => getUniqueYears(orders), [orders]);
  const rows = useMemo(
    () => groupPlanWithTasksByYear(getOrderTasks || [], getPaymentHistory || [], selectedYear),
    [getOrderTasks, getPaymentHistory, selectedYear]
  );

  const handleBillingHistory = (e) => {
    trackDownloadReceipts(e);
    navigate(`/dashboard/${businessEntityUuid}/subscription-hub/billing-history`);
  };

  if (error) {
    return (
      <PageContainer>
        <PageHeader title="Order History" />
        <ErrorState
          title="Unable to load order history"
          message="We're having trouble loading your order history. Please try again later."
        />
      </PageContainer>
    );
  }

  if (loading) {
    return (
      <PageContainer>
        <PageHeader title="Order History" />
        <StyledCard>
          <Box display="flex" justifyContent="center" alignItems="center" padding={6}>
            <CircularProgress />
          </Box>
        </StyledCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader title="Order History" />
      {!isWebView && (
        <Box width="100%" display="flex" justifyContent="flex-end" marginBottom="24px">
          <Button
            variant="contained"
            size={md ? 'medium' : 'small'}
            onClick={handleBillingHistory}
            clickstreamProps={{ elementName: 'download_receipts_button' }}
          >
            Download Receipts
          </Button>
        </Box>
      )}
      <StyledCard>
        <OrderTemplate
          rows={rows}
          selectedYear={selectedYear}
          years={years}
          setSelectedYear={setSelectedYear}
        />
      </StyledCard>
    </PageContainer>
  );
}
